import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteFooter, SiteHeader } from "@/components/site/SiteChrome";
import { ArticleCard } from "@/components/site/ArticleCard";
import { AdSlots } from "@/components/site/AdSlot";
import { listArticles } from "@/lib/site";
import { useDocumentMeta, useSite } from "@/hooks/useSite";
import { currentLanguage, t } from "@/i18n";

const PAGE = 9;

export default function Blog() {
  const site = useSite();
  const lang = currentLanguage();
  const [params, setParams] = useSearchParams();
  const tag = params.get("tag") || "";

  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useDocumentMeta({
    title: `${site?.blog?.title || t("nav.journal")} — ${site?.brand?.name || t("app.name")}`,
    description: site?.blog?.intro,
    url: typeof window !== "undefined" ? window.location.href : "",
  });

  useEffect(() => {
    setLoading(true);
    listArticles({ lang, tag, limit: PAGE })
      .then((data) => {
        setArticles(data.articles || []);
        setTotal(data.total || 0);
      })
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, [lang, tag]);

  const loadMore = async () => {
    const data = await listArticles({ lang, tag, limit: PAGE, skip: articles.length });
    setArticles((current) => [...current, ...(data.articles || [])]);
  };

  return (
    <div className="min-h-screen">
      <div className="hero-wash">
        <SiteHeader brandName={site?.brand?.name} tagline={site?.brand?.tagline} />
        <div className="mx-auto max-w-[1180px] px-6 pb-14 pt-12">
          <p className="eyebrow">{site?.blog?.title || t("nav.journal")}</p>
          <h1 className="mt-3 max-w-3xl text-4xl font-semibold leading-[1.08] tracking-tight sm:text-5xl">
            {site?.blog?.intro || t("site.latest")}
          </h1>
          {tag ? (
            <button
              type="button"
              onClick={() => setParams({})}
              data-testid="clear-tag"
              className="mt-5 rounded-full px-3 py-1 text-[12.5px]"
              style={{ backgroundColor: "var(--paper-2)", color: "var(--ink-soft)" }}
            >
              {tag} ×
            </button>
          ) : null}
        </div>
      </div>

      <main className="mx-auto max-w-[1180px] px-6 py-14">
        {loading ? (
          <p className="flex items-center gap-2 text-[13.5px] text-[color:var(--muted-ink)]">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("site.loading")}
          </p>
        ) : articles.length ? (
          <>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
            {articles.length < total ? (
              <div className="mt-10 flex justify-center">
                <Button variant="outline" onClick={loadMore} data-testid="load-more-articles">
                  {t("site.loadMore")}
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <p className="text-[14px] text-[color:var(--ink-soft)]" data-testid="no-articles">
            {t("site.noArticles")}
          </p>
        )}

        <AdSlots site={site} placement="blog_list" />
      </main>

      <SiteFooter site={site} />
    </div>
  );
}
